import html2canvas from 'html2canvas';
import QRCode from 'qrcode';

export async function generateReceipt(booking) {
  const formatSlot = (slot) => {
    const hour = parseInt(slot.split(':')[0]);
    const suffix = hour >= 12 ? 'PM' : 'AM';
    const display = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${display}:00 ${suffix}`;
  };

  const typeName = booking.booking_type === 'rush' ? 'Rush Hours' : booking.booking_type === 'mixed' ? 'Mixed Hours' : 'Happy Hours';
  const slotsArr = (booking.time_slots || []).map(formatSlot);
  const bookingId = booking.booking_id || 'N/A';
  const date = booking.booking_date || 'N/A';
  const hours = booking.total_hours || booking.time_slots?.length || 0;
  const total = booking.total_amount || 0;
  const status = (booking.status || 'pending').toUpperCase();
  const name = booking.customer_name || 'N/A';
  const email = booking.customer_email || 'N/A';
  const phone = booking.customer_phone || 'N/A';
  const txnId = booking.payment_id || booking.cf_order_id || 'N/A';
  const now = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  const time = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

  // Offline payment metadata
  const isOffline = booking.payment_status === 'offline';
  const paymentMode = booking.payment_mode || 'fully_paid';
  const isPartial = isOffline && paymentMode === 'partially_paid';
  const amountPaid = Number(booking.amount_paid != null ? booking.amount_paid : total) || 0;
  const amountDue = isPartial ? Math.max(0, total - amountPaid) : 0;

  // Custom time (admin offline) metadata
  const isCustomTime = booking.is_custom_time === true && booking.custom_start_time && booking.custom_end_time;
  const formatTime12 = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':').map(Number);
    const suf = h >= 12 ? 'PM' : 'AM';
    const dh = h > 12 ? h - 12 : h === 0 ? 12 : h;
    return `${dh}:${String(m).padStart(2, '0')} ${suf}`;
  };
  const customRange = isCustomTime ? `${formatTime12(booking.custom_start_time)} – ${formatTime12(booking.custom_end_time)}` : '';

  // Rate override (admin custom rate, e.g. VIP / Discount)
  const rateOverride = booking.rate_override_applied === true;
  const rateLabel = rateOverride ? (booking.rate_label || 'Custom rate') : '';
  const hourlyRate = Number(booking.hourly_rate || 0);

  const HAPPY_SET = new Set(['03:00','04:00','05:00','06:00','07:00','08:00','09:00','10:00','11:00','12:00','13:00','14:00']);
  const slots = booking.time_slots || [];
  const happyCount = slots.filter(s => HAPPY_SET.has(s)).length;
  const rushCount = slots.filter(s => !HAPPY_SET.has(s)).length;

  const statusColor = booking.status === 'confirmed' ? '#1b6e2a' : booking.status === 'pending' ? '#b8860b' : '#b71c1c';

  // Generate unique verification hash
  const hashInput = `${bookingId}-${date}-${total}-${txnId}`;
  const encoder = new TextEncoder();
  const data = encoder.encode(hashInput);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const verifyHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('').slice(0, 16).toUpperCase();

  // Generate QR code
  const qrData = `https://maldaturf.in/verify?hash=${verifyHash}&id=${bookingId}`;
  let qrDataUrl = '';
  try {
    qrDataUrl = await QRCode.toDataURL(qrData, { width: 80, margin: 1, color: { dark: '#1a1a1a', light: '#ffffff' } });
  } catch (e) {
    console.error('QR generation failed:', e);
  }

  const container = document.createElement('div');
  container.style.cssText = 'position:fixed;left:-9999px;top:0;z-index:-1;';
  document.body.appendChild(container);

  const slotsHtml = slotsArr.map(s =>
    `<span style="display:inline-flex;align-items:center;padding:4px 0;font-size:13px;font-weight:500;color:#333;margin-right:12px;line-height:1;">${s}</span>`
  ).join('<span style="color:#ccc;margin-right:12px;">·</span>');

  container.innerHTML = `
    <div id="receipt-capture" style="width:480px;background:#fff;overflow:hidden;font-family:Inter,system-ui,-apple-system,sans-serif;color:#1a1a1a;">

      <!-- Dark header -->
      <div style="background:#0a0a0a;padding:28px 32px;display:flex;justify-content:space-between;align-items:center;border-radius:0;">
        <div>
          <div style="font-size:24px;font-weight:800;color:#fff;letter-spacing:-0.5px;">Malda <span style="color:#16A34A;">Turf</span></div>
          <div style="font-size:9px;color:#666;text-transform:uppercase;letter-spacing:3px;margin-top:4px;">Premium Sports Facility</div>
        </div>
        <div style="text-align:right;">
          <div style="font-size:9px;color:#555;text-transform:uppercase;letter-spacing:2px;font-weight:600;">Booking Receipt</div>
          <div style="font-size:14px;font-weight:600;color:#fff;margin-top:4px;">#${bookingId.replace('book_', '')}</div>
        </div>
      </div>

      <!-- Green accent -->
      <div style="height:3px;background:#16A34A;"></div>

      <!-- Content -->
      <div style="padding:28px 32px 24px;">

        <!-- Status + Date row -->
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:20px;">
          <div style="display:flex;align-items:center;gap:8px;line-height:1;">
            <span style="font-size:12px;font-weight:700;letter-spacing:0.8px;color:${statusColor};text-transform:uppercase;">${status}</span>
            <span style="font-size:12px;color:#888;">·</span>
            <span style="font-size:12px;font-weight:600;color:#555;">${typeName}</span>
          </div>
          <div style="font-size:11px;color:#999;">${now}, ${time}</div>
        </div>

        <!-- Booking details -->
        <div style="background:#f9fafb;border:1px solid #eee;padding:20px 24px;margin-bottom:16px;border-radius:10px;display:flex;justify-content:space-between;align-items:center;">
          <div>
            <div style="font-size:20px;font-weight:800;color:#1a1a1a;letter-spacing:-0.3px;">${date}</div>
            <div style="font-size:12px;color:#888;margin-top:4px;">${isCustomTime ? `${customRange} · ${hours} hr${hours !== 1 ? 's' : ''}` : `${hours} hr${hours !== 1 ? 's' : ''}${happyCount > 0 ? ` · Happy Hours × ${happyCount}` : ''}${rushCount > 0 ? ` · Rush Hours × ${rushCount}` : ''}`}</div>
          </div>
          <div style="text-align:right;">
            <div style="font-size:28px;font-weight:800;color:#1a1a1a;letter-spacing:-1px;">₹${total}</div>
            ${rateOverride ? `<div style="font-size:9px;color:#16A34A;text-transform:uppercase;letter-spacing:1.5px;margin-top:2px;font-weight:700;">${rateLabel} · ₹${hourlyRate}/hr</div>` : ''}
            ${isOffline && !rateOverride ? `<div style="font-size:9px;color:#888;text-transform:uppercase;letter-spacing:1.5px;margin-top:2px;font-weight:600;">${isPartial ? 'Partially Paid' : 'Fully Paid'}</div>` : ''}
            ${isOffline && rateOverride ? `<div style="font-size:9px;color:#888;text-transform:uppercase;letter-spacing:1.5px;margin-top:1px;font-weight:600;">${isPartial ? 'Partially Paid' : 'Fully Paid'}</div>` : ''}
          </div>
        </div>

        ${isOffline ? `
        <!-- Payment summary (offline only) -->
        <div style="border:1px solid ${isPartial ? '#FACC15' : '#16A34A'};border-radius:10px;padding:16px 20px;margin-bottom:16px;background:${isPartial ? '#FEFCE8' : '#F0FDF4'};">
          <div style="font-size:9px;text-transform:uppercase;letter-spacing:2px;color:${isPartial ? '#854D0E' : '#166534'};font-weight:700;margin-bottom:10px;">Payment Status — ${isPartial ? 'Partially Paid' : 'Fully Paid'}</div>
          <div style="display:flex;justify-content:space-between;gap:12px;align-items:center;">
            <div style="flex:1;">
              <div style="font-size:9px;color:#999;text-transform:uppercase;letter-spacing:1px;font-weight:500;margin-bottom:2px;">Total</div>
              <div style="font-size:14px;font-weight:700;color:#1a1a1a;">₹${total}</div>
            </div>
            <div style="flex:1;border-left:1px solid ${isPartial ? '#FDE68A' : '#BBF7D0'};padding-left:12px;">
              <div style="font-size:9px;color:#999;text-transform:uppercase;letter-spacing:1px;font-weight:500;margin-bottom:2px;">Paid</div>
              <div style="font-size:14px;font-weight:700;color:#16A34A;">₹${amountPaid}</div>
            </div>
            ${isPartial ? `
            <div style="flex:1;border-left:1px solid #FDE68A;padding-left:12px;">
              <div style="font-size:9px;color:#999;text-transform:uppercase;letter-spacing:1px;font-weight:500;margin-bottom:2px;">Due</div>
              <div style="font-size:14px;font-weight:700;color:#CA8A04;">₹${amountDue}</div>
            </div>` : ''}
          </div>
          ${isPartial ? `<div style="margin-top:10px;padding-top:10px;border-top:1px dashed #FDE68A;font-size:10px;color:#854D0E;font-weight:500;">Balance ₹${amountDue} payable at turf after play.</div>` : ''}
        </div>` : ''}

        <!-- Time slots / Custom window -->
        <div style="margin-bottom:16px;">
          <div style="font-size:9px;text-transform:uppercase;letter-spacing:2px;color:#bbb;font-weight:600;margin-bottom:10px;">${isCustomTime ? 'Custom Time Window' : 'Time Slots'}</div>
          ${isCustomTime
            ? `<div style="font-size:15px;font-weight:700;color:#1a1a1a;letter-spacing:-0.2px;">${customRange} <span style="font-size:11px;color:#888;font-weight:500;margin-left:8px;">${hours} hr${hours !== 1 ? 's' : ''}</span></div>`
            : `<div style="display:flex;flex-wrap:wrap;gap:0;">${slotsHtml}</div>`
          }
        </div>

        <!-- Customer card -->
        <div style="border:1px solid #eee;padding:20px;margin-bottom:16px;border-radius:10px;">
          <div style="font-size:9px;text-transform:uppercase;letter-spacing:2px;color:#bbb;font-weight:600;margin-bottom:16px;">Customer</div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px;">
            <div>
              <div style="font-size:9px;text-transform:uppercase;letter-spacing:1px;color:#aaa;font-weight:500;margin-bottom:3px;">Name</div>
              <div style="font-size:14px;font-weight:600;color:#1a1a1a;">${name}</div>
            </div>
            <div>
              <div style="font-size:9px;text-transform:uppercase;letter-spacing:1px;color:#aaa;font-weight:500;margin-bottom:3px;">Phone</div>
              <div style="font-size:13px;font-weight:500;color:#1a1a1a;">${phone}</div>
            </div>
          </div>
          <div style="margin-top:14px;">
            <div style="font-size:9px;text-transform:uppercase;letter-spacing:1px;color:#aaa;font-weight:500;margin-bottom:3px;">Email</div>
            <div style="font-size:13px;font-weight:500;color:#1a1a1a;">${email}</div>
          </div>
        </div>

        <!-- QR Verification -->
        <div style="border:1px solid #eee;padding:14px 20px;border-radius:10px;display:flex;align-items:center;gap:16px;">
          ${qrDataUrl ? `<img src="${qrDataUrl}" style="width:64px;height:64px;flex-shrink:0;" />` : ''}
          <div>
            <div style="font-size:9px;text-transform:uppercase;letter-spacing:1.5px;color:#bbb;font-weight:600;margin-bottom:4px;">Verification</div>
            <div style="font-size:11px;font-weight:600;color:#555;font-family:monospace;letter-spacing:0.5px;">${verifyHash}</div>
            <div style="font-size:9px;color:#ccc;margin-top:3px;">Scan QR to verify booking</div>
          </div>
        </div>

      </div>

      <!-- Footer -->
      <div style="border-top:1px solid #eee;padding:16px 32px;display:flex;justify-content:space-between;align-items:center;">
        <div style="font-size:10px;color:#bbb;">Malda Turf · Malda, West Bengal</div>
        <div style="font-size:10px;color:#bbb;text-align:right;">+91 89186 95356 · maldaturf@gmail.com</div>
      </div>

    </div>
  `;

  try {
    const element = container.querySelector('#receipt-capture');
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
    });

    const link = document.createElement('a');
    link.download = `MaldaTurf_Receipt_${bookingId}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  } catch (err) {
    console.error('Receipt generation failed:', err);
    alert('Could not generate receipt. Please try again.');
  } finally {
    document.body.removeChild(container);
  }
}
